import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, addDoc, doc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import '../styles/ParentInfoModal.css';

interface Props {
  parent: { id: string; displayName?: string; email?: string };
  existingStudentIds: string[];
  onClose: () => void;
  onLinked: () => void;
}

const LinkStudentsModal: React.FC<Props> = ({ parent, existingStudentIds, onClose, onLinked }) => {
  const [students, setStudents] = useState<Array<{ id: string; displayName: string; email?: string }>>([]);
  const [queryText, setQueryText] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const all = usersSnap.docs
          .map(d => ({ id: d.id, ...(d.data() as any) }))
          .filter(u => u.role === 'student')
          .map(u => ({ id: u.id, displayName: u.displayName || 'Student', email: u.email }));
        setStudents(all);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    return students.filter(s =>
      !existingStudentIds.includes(s.id) &&
      (!q || s.displayName.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q))
    );
  }, [students, queryText, existingStudentIds]);

  const toggle = (id: string) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const submit = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (ids.length === 0) { onClose(); return; }
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      for (const studentId of ids) {
        // 1) Create link doc
        await addDoc(collection(db, 'parentStudentLinks'), {
          parentId: parent.id,
          studentId,
          relationship: 'parent',
          createdAt: now
        });

        // 2) Mirror on the student user (multi-parent support via arrayUnion)
        const studentRef = doc(db, 'users', studentId);
        await updateDoc(studentRef, {
          parentIds: arrayUnion(parent.id),
          parentEmail: parent.email || null
        });

        // 3) Append to parent's studentIds (multi-student support)
        const parentRef = doc(db, 'users', parent.id);
        await updateDoc(parentRef, { studentIds: arrayUnion(studentId) });
      }
      onLinked();
    } catch (e) {
      void window.appAlert('Failed to link students.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="parent-info-modal">
        <div className="modal-header">
          <div className="header-info">
            <h3>Link Students to {parent.displayName || 'Parent'}</h3>
            <span className="parent-email">{parent.email}</span>
          </div>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        <div style={{ padding: '1rem 1.5rem' }}>
          <input
            type="text"
            placeholder="Search students by name or email"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            className="filter-input"
            style={{ width: '100%', marginBottom: '1rem' }}
          />

          {loading ? (
            <div className="loading-spinner">Loading students…</div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {filtered.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggle(s.id)} />
                  <div style={{ fontWeight: 600 }}>{s.displayName}</div>
                  <div style={{ color: '#6b7280' }}>{s.email}</div>
                </label>
              ))}
              {filtered.length === 0 && (
                <div className="no-results">No students match your search.</div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button className="retry-button" onClick={onClose}>Cancel</button>
            <button className="view-button" onClick={submit} disabled={submitting}>{submitting ? 'Linking…' : 'Link Selected'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkStudentsModal;


